import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Link, Navigate } from "../lib/router";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { MaskedLines, Overline, Reveal } from "../components/motion";
import { PButton } from "../components/forms";
import { EditionSelector, ProductStage } from "../components/editions";
import { CraftSection } from "../components/CraftSection";
import { CRAFT, DEFAULT_EDITION, EDITIONS, REFORMERS, findEdition, productImage, resolveSlug } from "../data/products";

export default function ReformerDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const canonical = resolveSlug(slug);
  const product = REFORMERS.find((p) => p.slug === canonical);
  const [edition, setEdition] = useState(findEdition(searchParams.get("edition") || DEFAULT_EDITION).code);
  const ed = findEdition(edition);
  useMeta(product ? `${product.name} — Pilates Reformer | PILATES ON.` : "PILATES ON.", product ? `${product.desc[lang]} ${t.common.priceOnRequest}.` : "");

  if (!product) return <Navigate to="/reformer" replace />;
  if (canonical !== slug) return <Navigate to={`/reformer/${canonical}${searchParams.toString() ? `?${searchParams}` : ""}`} replace />;

  const specs = [
    [t.common.material, product.material[lang], "detail-spec-material"],
    [t.common.dims, product.dims, "detail-spec-dims"],
    [t.common.edition, ed.label[lang], "detail-spec-edition"],
  ];
  const quoteUrl = `/anfrage?produkt=${product.slug}&edition=${edition}`;

  return (
    <div data-testid={`detail-page-${product.slug}`}>
      <div className="mx-auto max-w-[1400px] px-6 pt-28 md:px-12 md:pt-32 lg:px-20">
        <Link to="/reformer" data-testid="detail-back" className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#66625A] transition-colors duration-300 hover:text-[#1C1C1A]">
          <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-1.5" />
          {t.detail.back}
        </Link>
      </div>

      <section className="mt-8 md:mt-10">
        <ProductStage product={product} edition={edition} testId="detail-hero" className="h-[62vh] min-h-[420px] px-6 py-12 md:h-[80vh] md:px-20 md:py-20" imgClassName="drop-shadow-[0_40px_60px_rgba(0,0,0,0.22)]">
          <div className={`absolute left-6 top-6 z-[2] font-mono text-[10px] uppercase tracking-[0.3em] md:left-12 md:top-10 lg:left-20 ${ed.dark ? "text-[#FAF8F5]/70" : "text-[#66625A]"}`}>
            PILATES ON. · {product.name}
          </div>
          <div data-testid="detail-hero-edition" className={`absolute bottom-6 right-6 z-[2] flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] md:bottom-10 md:right-12 lg:right-20 ${ed.dark ? "text-[#FAF8F5]/80" : "text-[#66625A]"}`}>
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: ed.swatch }} />
            {ed.label[lang]}
          </div>
        </ProductStage>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:px-12 md:py-28 lg:grid-cols-12 lg:px-20">
        <div className="lg:col-span-6">
          <h1 data-testid="detail-title" className="font-serif text-4xl font-light leading-[0.98] tracking-tight text-[#1C1C1A] sm:text-5xl md:text-7xl">
            <MaskedLines lines={product.titleLines} mount delay={0.1} />
          </h1>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={0.2}>
            <p data-testid="detail-desc" className="max-w-md text-lg font-light leading-relaxed text-[#66625A] md:text-xl">{product.desc[lang]}</p>
            <p data-testid="detail-price" className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{t.common.priceOnRequest}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="detail-editions" className="border-y border-[#1C1C1A]/10">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:px-12 md:py-28 lg:grid-cols-12 lg:px-20">
          <div className="lg:col-span-5">
            <Reveal y={14}>
              <Overline className="mb-6">{t.common.finishesLabel}</Overline>
            </Reveal>
            <h2 data-testid="detail-editions-title" className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-[#1C1C1A] md:text-6xl">
              <MaskedLines lines={t.detail.chooseFinish} />
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-[#66625A]">{t.detail.finishHint}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <EditionSelector value={edition} onChange={setEdition} size="lg" testPrefix="edition" />
              <div className="mt-10 border-t border-[#1C1C1A]/10 pt-6">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{t.detail.selected}</p>
                <p data-testid="detail-edition-name" className="font-serif text-2xl font-light text-[#1C1C1A] md:text-3xl">{ed.label[lang]}</p>
                <p data-testid="detail-edition-desc" className="mt-2 text-sm font-light text-[#66625A]">{ed.desc[lang]}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="detail-gallery" className="mx-auto max-w-[1400px] px-6 pt-20 md:px-12 md:pt-28 lg:px-20">
        <Reveal y={14}>
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <Overline>{t.detail.gallery}</Overline>
            <p className="text-sm font-light text-[#66625A]">{t.detail.galleryHint}</p>
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-5">
          {EDITIONS.map((e, i) => (
            <Reveal key={e.code} delay={i * 0.06}>
              <button
                type="button"
                data-testid={`gallery-${e.code}`}
                aria-pressed={edition === e.code}
                onClick={() => setEdition(e.code)}
                className={`group block w-full text-left transition-opacity duration-500 ${edition === e.code ? "opacity-100" : "opacity-70 hover:opacity-100"}`}
              >
                <div className={`relative aspect-square overflow-hidden ring-1 transition-colors duration-500 ${edition === e.code ? "ring-[#1C1C1A]" : "ring-transparent"}`} style={{ backgroundColor: e.backdrop }}>
                  <img src={productImage(product, e.code)} alt={`${product.name} – ${e.label[lang]}`} className="h-full w-full object-contain p-4 transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
                </div>
                <p className={`mt-3 font-mono text-[10px] uppercase tracking-[0.25em] ${edition === e.code ? "text-[#1C1C1A]" : "text-[#66625A]"}`}>{e.name}</p>
              </button>
            </Reveal>
          ))}
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2 md:gap-6">
          {[CRAFT.wood, CRAFT.upholstery].map((c) => (
            <Reveal key={c.image}>
              <div className="group overflow-hidden">
                <img src={c.image} alt={c.title[lang]} className="aspect-[16/10] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="detail-details" className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:px-12 md:py-28 lg:grid-cols-12 lg:px-20">
        <div className="lg:col-span-4">
          <Reveal y={14}>
            <Overline>{t.detail.details}</Overline>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal delay={0.1}>
            <dl data-testid="detail-specs">
              {specs.map(([label, value, tid]) => (
                <div key={label} className="flex items-baseline justify-between gap-8 border-b border-[#1C1C1A]/10 py-6">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">{label}</dt>
                  <dd data-testid={tid} className="text-right text-[15px] text-[#1C1C1A]">{value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-[#66625A]/70">{t.detail.specsNote}</p>
            <div className="mt-12 flex flex-wrap items-center gap-8">
              <p data-testid="detail-price-label" className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">{t.common.priceOnRequest}</p>
              <PButton to={quoteUrl} variant="solid" data-testid="detail-quote-cta">{t.common.quote}</PButton>
            </div>
          </Reveal>
        </div>
      </section>

      <CraftSection />

      <section data-testid="detail-offer" className="bg-[#1C1C1A] text-[#FAF8F5]">
        <div className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32 lg:px-20">
          <Reveal y={14}>
            <Overline light className="mb-8">{t.detail.offerOverline}</Overline>
          </Reveal>
          <h2 data-testid="detail-offer-title" className="font-serif text-4xl font-light leading-[1.05] tracking-tight md:text-6xl">
            <MaskedLines lines={t.detail.offerTitle} />
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-[#FAF8F5]/70 md:text-lg">{t.detail.offerText}</p>
            <div className="mt-10">
              <PButton to={quoteUrl} variant="light" data-testid="detail-offer-cta">
                {t.detail.offerBtn} — {product.name} · {ed.label[lang]}
              </PButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
